
import Vue from 'vue';
import Component from 'vue-class-component';
import HelpdeskAssigneePicker from '@/components/helpdesk/HelpdeskAssigneePicker.vue';
import {Prop, Watch} from 'vue-property-decorator';
import RichTextEditor from '@/components/helpdesk/RichTextEditor.vue';
import {ITicketType} from '@/store/modules/helpdeskTicketTypesModule';
import {IHelpdeskEmail} from '@/store/modules/helpdeskInboxModule';
import {ticketStatusLabel, ticketStatusShortLabel} from '@/helpers/helpdeskTicketStatus';

@Component({name: 'TicketCreateModal', components: {RichTextEditor, HelpdeskAssigneePicker}})
export default class TicketCreateModal extends Vue {
  @Prop({required: true}) open!: boolean;
  @Prop({default: null})  email!: IHelpdeskEmail | null;
  // Prefill when the ticket doesn't come from an email (e.g. a WhatsApp chat).
  @Prop({default: ''})    defaultSubject!: string;
  @Prop({default: ''})    defaultDescription!: string;

  internalOpen = this.open;
  valid        = false;
  saving       = false;
  loadingTypes = false;
  creatingType = false;
  typeSearch: string | null = null;

  form = {
    subject:        '',
    ticket_type_id: null as number | null,
    assignee_email: '',
    description:    '',
  };

  @Watch('open')
  onOpenChanged(val: boolean) {
    this.internalOpen = val;
    if (val) {
      this.form.subject        = this.email ? this.email.subject : this.defaultSubject;
      this.form.ticket_type_id = null;
      this.form.assignee_email = '';
      this.form.description    = this.defaultDescription;
      this.loadTypes();
    }
  }

  get reporterEmail(): string {
    const userData = this.$store.getters['authModule/userData'];
    return (userData && userData.email) ? userData.email : '—';
  }

  get ticketTypes(): ITicketType[] {
    return this.$store.getters['helpdeskTicketTypesModule/types'];
  }

  get openStatusLabel(): string {
    return ticketStatusShortLabel(this.$i18n, 'open');
  }

  get openStatusTitle(): string {
    return ticketStatusLabel(this.$i18n, 'open');
  }

  // What was typed, when no existing type has exactly that name (case-insensitive).
  get newTypeName(): string {
    const name = (this.typeSearch || '').trim();
    if (!name) return '';
    const lower = name.toLowerCase();
    return this.ticketTypes.some(t => t.name.toLowerCase() === lower) ? '' : name;
  }

  // Enter still picks a highlighted type; it only creates when nothing in the list matches.
  onTypeEnter() {
    const search = (this.typeSearch || '').trim().toLowerCase();
    if (!this.newTypeName || this.ticketTypes.some(t => t.name.toLowerCase().indexOf(search) !== -1)) return;
    this.createType();
  }

  createType() {
    const name = this.newTypeName;
    if (!name || this.creatingType) return;
    this.creatingType = true;
    const subs = (this as any).$ons({
      'backend.call.OK.typeCreated': (type: ITicketType) => {
        (this as any).$offs(subs);
        this.creatingType = false;
        this.loadTypes();
        this.form.ticket_type_id = type.id;
        this.typeSearch = null;
        this.$uiBus.emit('success', this.$t('helpdesk.ticketCreate.typeCreated', {name: type.name}));
      },
      'backend.call.KO.typeCreated': () => {
        (this as any).$offs(subs);
        this.creatingType = false;
      },
    });
    this.$store.dispatch('helpdeskTicketTypesModule/createType', {name});
  }

  loadTypes() {
    this.loadingTypes = true;
    this.$store.dispatch('helpdeskTicketTypesModule/fetchTypes', {all: true})
      .finally(() => { this.loadingTypes = false; });
  }

  cancel() {
    this.$emit('update:open', false);
  }

  submit() {
    const formRef = this.$refs.form as any;
    if (!formRef.validate()) return;

    this.saving = true;

    const payload: any = {
      subject:        this.form.subject,
      ticket_type_id: this.form.ticket_type_id || undefined,
      reporter_email: this.reporterEmail !== '—' ? this.reporterEmail : undefined,
      assignee_email: this.form.assignee_email || undefined,
      description:    this.form.description || undefined,
    };

    if (this.email) {
      payload.email_id = this.email.id;
    }

    this.$store.dispatch('helpdeskTicketsModule/createTicket', payload);

    // escuchamos el resultado una sola vez
    const unsubOK = (this as any).$ons({
      'backend.call.OK.ticketCreated': (ticket: any) => {
        this.saving = false;
        this.$emit('created', ticket);
        this.$emit('update:open', false);
        (this as any).$offs(unsubOK);
        (this as any).$offs(unsubKO);
      },
      'backend.call.KO.ticketCreated': () => {
        this.saving = false;
        (this as any).$offs(unsubOK);
        (this as any).$offs(unsubKO);
      },
    });
    const unsubKO = unsubOK; // misma referencia del objeto
  }
}
